import { RadioGroup } from '@headlessui/react';
import {
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  BellIcon,
  CalendarIcon,
  ChartBarIcon,
  CheckIcon,
  LockClosedIcon,
  MapPinIcon,
  PauseCircleIcon,
} from '@heroicons/react/24/outline';
import { Button, Col, Empty, Grid, Row, Space, Spin } from 'antd';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Header from '@/renderer/Header';
import useMe from '@/renderer/utils/useMe';
import { usePageContext } from '@/renderer/utils/usePageContext';
import useRequest from '@/renderer/utils/useRequest';

import calculatePrice from './calculatePrice';
import StripeButton from './components/StripeButton';
import styles from './Order.module.less';
import { getMyOrders, getProducts, getServices } from './requests';
import { Progress } from '@/components/UI/progress';

const { useBreakpoint } = Grid;

const BitcoinButton: React.FC<{
  user: API.User;
  product: API.MembershipProduct;
  index: number;
  fullWidth?: boolean;
}> = ({ user, product, index }) => {
  const mailto = `mailto:admin@raspicks.com?subject=${user.username} is requesting Bitcoin instructions for ${product.title} (ID#${product.id} ${product.offers?.[index]?.plays} plays)`;

  return (
    <a href={mailto}>
      <button className="flex w-full cursor-pointer justify-center rounded border-0 bg-white px-3 py-2.5 text-sm font-semibold leading-6 text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-900/10 hover:bg-zinc-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 disabled:cursor-not-allowed disabled:opacity-50 data-[loading=true]:animate-pulse data-[loading=true]:cursor-progress dark:bg-white/10 dark:text-white dark:ring-white/20 hover:dark:bg-white/20">
        Bitcoin
      </button>
    </a>
  );
};

const PriceInfo: React.FC<{
  locale: string;
  originalPrice: number;
  price: number;
  stats?: API.MembershipProductStats;
  variety?: API.MembershipProductOffer;
  playInfo?: boolean;
  playsFor?: number;
  plus?: boolean;
}> = ({ locale, price, variety, playInfo, stats, playsFor, plus }) => {
  if (variety?.auto) {
    return (
      <>
        {variety?.earlyBird && variety?.sport === 'cfb' && (
          <>
            <span className={styles.special}>Early bird special</span>
            <p className={styles.specialEnds}>
              ${variety?.earlyBird} off &amp; exclusive Week 1 and season-long future releases
            </p>
            <p className={styles.specialEnds}>Offer ends July 21 at 9 a.m. PT</p>
          </>
        )}
        <div>
          <span className="mb-0 block text-3xl font-semibold dark:text-white">
            {playsFor && `${playsFor}${plus ? '+' : ''} play${playsFor > 1 ? 's' : ''} for `}
            {price?.toLocaleString(locale, {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
            {variety?.prorated && <span className={styles.proratedAsterisk}>*</span>}{' '}
            {playInfo && (
              <span className={styles.playInfo}>
                / {variety.plays - (stats || baseStats).played}+ plays
              </span>
            )}
            {variety?.earlyBird && (
              <span className={styles.originalPrice}>
                ${price + variety?.earlyBird + (variety?.comboSavings || 0)}
              </span>
            )}
          </span>
          {variety?.comboSavings && (
            <small>
              ${variety?.comboSavings} combo savings
              {variety?.earlyBird && ` + $${variety?.earlyBird} early bird discount`}
            </small>
          )}
          {variety?.comboBreakdown && <small>({variety?.comboBreakdown})</small>}
        </div>
      </>
    );
  }

  return (
    <>
      {variety?.earlyBird && variety?.sport === 'cfb' && (
        <>
          <span className={styles.special}>Early bird special</span>
          <p className={styles.specialEnds} style={{ marginBottom: 0 }}>
            ${variety?.earlyBird} off &amp; exclusive Week 1 and season-long future releases
          </p>
          <p className={styles.specialEnds}>
            <em>Offer ends July 21 at 9 a.m. PT</em>
          </p>
        </>
      )}
      <div>
        <span className="mb-0 block text-4xl font-semibold dark:text-white">
          {playsFor && `${playsFor}${plus ? '+' : ''} play${playsFor > 1 ? 's' : ''} for `}
          {price?.toLocaleString(locale, {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
          {variety?.prorated && <span className={styles.proratedAsterisk}>*</span>}{' '}
          {playInfo && <span className={styles.playInfo}>/ {variety?.plays} plays</span>}
          {variety?.earlyBird && (
            <span className={styles.originalPrice}>
              ${price + variety?.earlyBird + (variety?.comboSavings || 0)}
            </span>
          )}
        </span>
        {variety?.comboSavings && (
          <small>
            ${variety?.comboSavings} combo savings
            {variety?.earlyBird && ` + $${variety?.earlyBird} early bird discount`}
          </small>
        )}
        {variety?.comboBreakdown && <small>({variety?.comboBreakdown})</small>}
      </div>
    </>
  );
};

const baseStats: API.MembershipProductStats = {
  canceled: 0,
  loss: 0,
  pending: 0,
  played: 0,
  push: 0,
  units: 0,
  win: 0,
};

const baseOffer: API.MembershipProductOffer = {
  id: [0],
  ids: [0],
  plays: 0,
  price: 0,
};

const Product: React.FC<{
  locale: string;
  product: API.MembershipProduct;
  stats: API.MembershipProductStats | undefined;
  discount: number;
  initialState: { currentUser?: API.User | null | undefined };
  playInfo?: boolean;
  orders?: API.MembershipOrder[];
}> = ({ locale, product, stats, discount, initialState, playInfo = false, orders = [] }) => {
  return (
    <>
      {(product.offers || []).map(
        (variety, index) =>
          !variety?.hidden &&
          Object.keys(variety).length > 0 && (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment
              key={`${variety.id}${variety.ids}${variety.plays}${variety.price}${variety.auto}`}
            >
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {variety?.soldOut ? (
                  <div className={styles.soldOut}>
                    <h2>Sold out</h2>
                    <hr className={styles.soldOutHr} />
                    <p>Interested in joining our next service?</p>
                    <Button type="primary" shape="round" size="large">
                      <a
                        href="https://ras.m-pages.com/dgOZ1d/order-page-waiting-list"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Sign up for mailing list
                      </a>
                    </Button>
                  </div>
                ) : (
                  <>
                    <PriceInfo
                      locale={locale}
                      variety={variety}
                      stats={stats || baseStats}
                      {...calculatePrice({ discount, stats: stats || baseStats, variety })}
                      playInfo={playInfo}
                    />
                    <div style={{ position: 'relative', zIndex: 10 }}>
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {initialState?.currentUser && (
                          <>
                            {!orders?.find(
                              (order) =>
                                order?.productId === product.id &&
                                order?.offer?.orderLimit &&
                                variety.orderLimit &&
                                variety.orderLimit <= order?.offer?.orderLimit,
                            ) ? (
                              <>
                                <StripeButton
                                  product={product}
                                  index={index}
                                  price={
                                    calculatePrice({ discount, stats: stats || baseStats, variety })
                                      .price
                                  }
                                  size="large"
                                  source="order"
                                />
                                <BitcoinButton
                                  user={initialState.currentUser}
                                  product={product}
                                  index={index}
                                  fullWidth={true}
                                />
                              </>
                            ) : (
                              <button className="flex w-full cursor-not-allowed justify-center rounded border-0 bg-white px-3 py-2.5 text-sm font-semibold leading-6 text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-900/10 hover:bg-zinc-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 disabled:cursor-not-allowed disabled:opacity-50 data-[loading=true]:animate-pulse data-[loading=true]:cursor-progress dark:bg-white/10 dark:text-white dark:ring-white/20 hover:dark:bg-white/10">
                                <CheckIcon className="mr-2 inline-block h-5 w-5" />
                                Offer claimed
                              </button>
                            )}
                          </>
                        )}
                        {!initialState?.currentUser && (
                          <a
                            href={`/signin?redirect=/order${
                              window.location.pathname === '/cfb' ? '?sport=cfb' : ''
                            }`}
                          >
                            {' '}
                            <button className="flex w-full cursor-pointer justify-center rounded border-0 bg-emerald-500 px-3 py-2.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-emerald-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 disabled:cursor-not-allowed disabled:opacity-50 data-[loading=true]:animate-pulse data-[loading=true]:cursor-progress dark:bg-emerald-500 hover:dark:bg-emerald-400">
                              Sign in to order
                            </button>
                          </a>
                        )}
                      </Space>
                    </div>
                  </>
                )}
              </Space>
              {variety?.orderLimit && (
                <div className="mb-4 mt-1 flex items-center text-xs text-zinc-500 dark:text-zinc-400">
                  <LockClosedIcon
                    className="mr-2 inline-block h-5 w-auto shrink-0"
                    aria-hidden="true"
                  />
                  <span style={{ marginLeft: '6px' }}>
                    Limited to {variety.orderLimit} order per customer.
                  </span>
                </div>
              )}
            </React.Fragment>
          ),
      )}
      {!!product.startDate && new Date(product.startDate).getTime() > Date.now() && (
        <h6>
          <CalendarIcon className="mr-2 inline-block h-5 w-5" /> Begins on{' '}
          {new Date(product.startDate)?.toLocaleDateString(locale)}
        </h6>
      )}
      {!!product.endDate && (
        <h6>
          <CalendarIcon className="mr-2 inline-block h-5 w-5" /> Runs through{' '}
          {new Date(product.endDate)?.toLocaleDateString(locale)}
        </h6>
      )}
    </>
  );
};

const NFLProduct: React.FC<{
  locale: string;
  product: API.MembershipProduct;
  discount: number;
  initialState: { currentUser?: API.User | null | undefined };
  orders?: API.MembershipOrder[];
}> = ({ locale, product, discount, initialState, orders }) => {
  const { lg } = useBreakpoint();
  const allInclusive = product.title === 'NFL Service Season Pass';
  const offer = product?.offers?.find((ofr) => ofr.nextRelease != undefined);
  const nextRelease = offer?.nextRelease;

  const allInclusiveIncludes = [
    {
      title: 'NFL regular and postseason',
      description: product?.description || '',
    },
  ];

  return (
    <Col xs={24} lg={11}>
      <div
        className={twMerge(
          'rounded bg-white p-8 shadow-lg ring-1 ring-zinc-900/5 dark:bg-white/5 dark:shadow-none dark:ring-white/20',
          allInclusive && 'z-10 bg-white dark:bg-zinc-700',
          lg && !allInclusive && 'border-r-0',
        )}
      >
        <div className="flex items-baseline justify-between">
          <span className="text-lg font-semibold">{product.title}</span>
          {product?.bubble && (
            <span className="flex w-fit shrink-0 items-center rounded-full bg-zinc-900/80 px-4 py-1 font-semibold text-white dark:bg-white/80 dark:text-zinc-900 md:ml-auto">
              <span>{product?.bubble}</span>
            </span>
          )}
        </div>
        <p className="mb-8 text-zinc-600 dark:text-zinc-400">
          {allInclusive
            ? 'Includes all releases for entire 2023 season'
            : `${product?.description}`}
        </p>
        <Product
          locale={locale}
          product={product}
          stats={product?.stats}
          discount={discount}
          initialState={initialState}
          key={product?.id}
          playInfo={!allInclusive}
          orders={orders}
        />
        <div className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
          Have any questions about the service?{' '}
          <a
            href="https://calendly.com/raspicks/ras-service-discussion?month=2023-08"
            target="_blank"
            rel="noreferrer"
          >
            Book a meeting
          </a>
        </div>
        {offer?.record && (
          <div className={styles.recordRow}>
            <ArrowTrendingUpIcon className="mr-2 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>Current record: {offer?.record}</span>
          </div>
        )}
        {/* <div className={styles.iconRow}>
          <CalendarIcon className="mr-2 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>Next scheduled release: {nextRelease ? `${nextRelease}` : 'TBD'}</span>
        </div> */}
        {allInclusive && (
          <div className={styles.includesList}>
            {allInclusiveIncludes.map(({ title, description }) => (
              <div key={title} className={styles.includesRow}>
                <CheckIcon
                  className="h-6 w-auto shrink-0 text-emerald-500 dark:text-emerald-400"
                  aria-hidden="true"
                />
                <div className={styles.includesItem}>
                  <span className="font-semibold">{title}</span>
                  <span className="inline-block text-zinc-500 dark:text-zinc-400">
                    {description}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        {offer?.prorated && <div className={styles.prorated}>*Prorated {offer?.prorated}</div>}
      </div>
    </Col>
  );
};

const NFL: React.FC<{
  locale: string;
  products?: API.MembershipProduct[];
  discount: number;
  initialState: { currentUser?: API.User | null | undefined };
  orders?: API.MembershipOrder[];
}> = ({ locale, products, discount, initialState, orders }) => {
  const [promoProduct, setPromoProduct] = useState<null | API.MembershipProduct>(null);

  useEffect(() => {
    if (products) {
      const foundPromoProduct = products.find(
        (product) => product.offers?.find((offer) => offer.promo),
      );

      if (foundPromoProduct) {
        const promoOffer = foundPromoProduct.offers.filter((offer) => offer.promo);

        foundPromoProduct.offers = promoOffer;
        setPromoProduct(foundPromoProduct);
      }
    }
  }, [products]);

  return (
    <>
      {promoProduct && (
        <div className="mx-auto mb-8 max-w-4xl rounded bg-emerald-400/20 p-4 px-8 ring-1 ring-emerald-400/50">
          <Row align="middle" gutter={[0, 16]}>
            <Col xs={24} sm={14} lg={16}>
              <div className="">
                <div className="inline-block font-semibold uppercase tracking-wide text-emerald-900 dark:text-emerald-500">
                  Special offer
                </div>
                <h5 className="mb-4 text-xl font-semibold dark:text-white">
                  Get 1 play and 1 prop for ${promoProduct?.offers[0].price}
                </h5>
                <p className={styles.promoSecondary}>{promoProduct?.includesDescription}</p>
              </div>
            </Col>
            <Col xs={24} sm={10} lg={8}>
              <Product
                locale={locale}
                key={promoProduct.title}
                product={promoProduct}
                discount={discount}
                initialState={initialState}
                stats={promoProduct.stats}
              />
            </Col>
          </Row>
        </div>
      )}
      {products && (
        <>
          <Row className={styles.nflContainer} align="middle" gutter={[0, 16]}>
            {products.map((product) => {
              return (
                product.id !== promoProduct?.id && (
                  <NFLProduct
                    locale={locale}
                    key={product.title}
                    product={product}
                    discount={discount}
                    initialState={initialState}
                    orders={orders}
                  />
                )
              );
            })}
          </Row>
        </>
      )}
      <div className={styles.additionalContent}>
        <p className="mx-auto max-w-prose text-xs text-zinc-500 dark:text-zinc-400">
          DISCLAIMER: Even though this service is targeted to all bettors, it should be expected
          that lines will move within seconds of each play being released. Sportsbooks will
          sometimes label RAS plays as &quot;sharp&quot; action and cut your limits or ban you from
          playing entirely. Despite our long-term winning history, we are not immune to variance,
          bad streaks, etc. It will happen. Do not overreact to good or bad streaks. Proper bankroll
          size, available outs, time and skills are needed to be successful and justify the cost of
          this service.
        </p>
        <hr className={styles.hr} />
        <div className="prose prose-sm prose-zinc max-w-prose dark:prose-invert prose-a:font-semibold prose-a:text-emerald-500 hover:prose-a:text-emerald-600 sm:mx-auto">
          <h4>User questions</h4>
          <h5>&quot;This is not the RAS I know! Why is this offering so different?&quot;</h5>
          <p>
            The Right Angle Sports service has been operating since 1996, and for over two decades,
            the service was run quietly targeting only the most serious bettors. However, in recent
            years, the sports betting industry in America has undergone significant transformation.
            There are now more bettors and more places to bet than ever before, which has increased
            the demand for trustworthy picks and good betting content. Although we have been
            hesitant to expand the service in the past due to concerns of release line availability
            at a large scale, we have changed our perspective following the success of our Release
            Shows. Thousands of people were able to obtain the release odds per show, making us
            believe that now is the right time, and <b>NFL is the right market</b>, to offer a new
            type of product that services a wider range of bettors.
          </p>
          <h5>&quot;What else is different?&quot;</h5>
          <p>
            In addition to offering new types of content not previously seen in RAS subscriptions,
            we have made three tweaks to the typical service:
          </p>
          <p>
            <b>1. Removal of prohibited sportsbooks</b>
          </p>
          <p>
            Since RAS started the service, we have prohibited playing releases at a number of
            sportsbooks. The reason for this rule was that some sportsbooks use auto movers, and by
            just one person playing at them, it will negatively impact the rest of the market.
            However, we have learned that this rule is becoming increasingly difficult to enforce.
            We have also learned through live release shows which did not have the restriction rule,
            thousands of bettors still have a very high rate of success in obtaining the release
            line. Therefore, we have changed our stance on this service rule for this NFL product.
          </p>
          <p>
            <b>2. Adjustment to the prorated refund</b>
          </p>
          <p>
            We have always offered a no-questions-asked, prorated refund option for all of our
            service options. This NFL season subscription is the first time we have offered an
            all-encompassing season-long option. While we will continue to offer the same refund
            policy as always, we have made a minor adjustment to the prorated refund process for
            this subscription. Since this subscription is not charged on a per-play basis, the
            refund amount will now be based on a time schedule. Starting with the first week of the
            preseason, the prorated refund amount will decrease by $75 each week. For example, if
            you submit a refund request after the third week of the preseason, the refund amount
            would be calculated as follows: (3 weeks x $75 per week decrease) - $795 full-season
            price = $570 refund amount.
          </p>
          <p>
            *Preseason Only: Any refunds for the preseason only will be prorated at $100 per week
            including the HOF game.
          </p>
          <p>
            <b>3. Removal of pause feature</b>
          </p>
          <p>
            Since this is not a service that is charged per release, we will not have the option to
            pause the service. The subscription is based on the full season duration which includes
            all releases.
          </p>
          <h5>&quot;Is RAS losing their edge and selling out for customers?&quot;</h5>
          <p>
            This is a point of criticism that we expect to hear considering the big difference in
            price. However, this notion could not be further from the truth. Our team is bigger than
            it has ever been in the past and we are coming off one of our best football seasons ever
            in 2022. The confidence we can win, and will continue to win is encouraging us to
            release more plays to more people, not the other way around.
          </p>
          <p>Our service lifetime record for NFL:</p>
          <ul>
            <li>
              <b>90-62 (59.2%) in the preseason</b>
            </li>
            <li>
              <b>64-48 (57.1%) in the regular season</b>
            </li>
          </ul>
          <hr />
          <h4>Service details</h4>
          <br />
          <p>
            Plays will be released as early as the market is reasonably mature, and will continue
            throughout the week as information comes in and derivatives (first half
            lines/moneylines) become available. Please be advised that while we attempt to make the
            service as easy to follow and routine as possible, market maturity isn&apos;t always
            consistent. We aim to provide the most value possible to our subscribers, so our release
            times may vary.
          </p>
          <p>
            As of now, we expect to release NFL regular season sides and totals on Mondays at
            approximately 8 a.m. PT. We will also likely have a midweek release window (likely
            Wednesday.)
          </p>
          <p>
            We will have a better idea of preseason release times as the season gets closer. We will
            aim for a core early-week release time, but due to the info-based nature of preseason,
            you should expect to see a few unplanned releases during the week. We will do our best
            to provide as much advance notice as possible.
          </p>
          <p>
            Second half plays should be considered possible during halftime of any NFL preseason or
            regular season game.
          </p>
          <h5>Release warnings</h5>
          <p>
            There will be a 3- to 5-minute warning before the release of any full game play. There
            will be a 1- to 2-minute warning before the release of any second half play. Warnings
            will be sent via email and text message.
          </p>
          <h5>Release method</h5>
          <p>
            All plays will be released at raspicks.com which is available via web or mobile browser.
            To ensure a level playing field and controlling synchronized delivery timing to all
            subscribers as much as possible, the plays will only be released at raspicks.com and not
            by email or text.
          </p>
          <p>
            Full game plays will use our Set Up and Go release method. When the release starts, you
            will be asked to set up a given play, for example:
          </p>
          <p>
            <i>Set up 102 Kansas City Chiefs -4.5</i>
          </p>
          <p>
            After about 25-35 seconds, a second message will appear: GO = confirm your bet (play is
            now official) or CANCEL = cancel your bet and wait for the next play.
          </p>
          <p>This allows you time to set up your bets as needed.</p>
          <p>Second half plays can be acted upon immediately upon receipt.</p>
          <p>
            Any release should not be considered over until you see a message saying
            &quot;Done&quot;.
          </p>
          <h5>Refund policy</h5>
          <p>
            Starting with the first week of the preseason, the prorated refund amount will decrease
            by $75 each week. For example, if you submit a refund request after the third week of
            the preseason, the refund amount would be calculated as follows: (3 weeks x $75 per week
            decrease) - $795 full-season price = $570 refund amount.
          </p>
          <p>
            *Preseason Only: Any refunds for the preseason only will be prorated at $100 per week
            including the HOF game.
          </p>
          <h5>Customer service</h5>
          <p>
            Please do not hesitate to contact us with any questions, comments or concerns at{' '}
            <a href="mailto:admin@raspicks.com">admin@raspicks.com</a>. All inquiries will be
            answered promptly.
          </p>
        </div>
      </div>
    </>
  );
};

const OffersProduct: React.FC<{
  locale: string;
  product: API.MembershipProduct;
  discount: number;
  initialState: { currentUser?: API.User | null | undefined };
}> = ({ locale, product, discount, initialState }) => {
  const { offers, stats } = product;
  const [offer, setOffer] = useState<API.MembershipProductOffer | undefined>(offers[0]);
  // const [quantities, setQuantities] = useState(new Set<number>());
  // const [selectedQuantity, setSelectedQuantity] = useState(offers[0]?.plays);
  // const [starterAddOn, setStarterAddOn] = useState(true);
  const [guarantee, setGuarantee] = useState(false);

  const played = stats?.played || 0;
  const [progress, setProgress] = React.useState((played / 75) * 100);

  // const [services, setServices] = useState(new Set());
  // const [selectedServices, setSelectedServices] = useState('all');
  // console.log(product);

  const USDollar = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  useEffect(() => {
    const selectedOffer = offers.find(
      (ofr) => guarantee === ofr.guarantee,
      // selectedServices === 'all' ? !!ofr.ids : selectedServices === ofr.id,
    );

    setOffer(selectedOffer);
  }, [
    offers,
    guarantee,
    // selectedServices,
  ]);

  const handleGuarnateeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGuarantee(e.target.checked);
  };

  return (
    <>
      {offer ? (
        <>
          <div className="col-span-12 max-w-2xl rounded bg-white shadow-lg ring-1 ring-zinc-900/5 dark:bg-white/5 dark:shadow-none dark:ring-white/20 lg:mx-0 lg:flex lg:max-w-none">
            <div className="p-8 sm:p-10 lg:flex-auto">
              <h2 className="text-2xl font-semibold">{product.title}</h2>
              <p className="mt-6 text-base leading-7 text-zinc-600 dark:text-zinc-400">
                {product?.description}
              </p>
              <div className="mt-6 flex items-center">
                <ChartBarIcon
                  className="mr-2 h-6 w-auto shrink-0 text-zinc-400 dark:text-zinc-500"
                  aria-hidden="true"
                />
                <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-300">
                  Minimum of 3 more plays will be released
                </p>
              </div>
              <div className="mt-6 flex items-center">
                <CalendarIcon
                  className="mr-2 h-6 w-auto shrink-0 text-zinc-400 dark:text-zinc-500"
                  aria-hidden="true"
                />
                <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-300">
                  Release windows
                </p>
              </div>
              <ul className="mb-8 mt-4 list-disc space-y-2 ps-4 text-xs">
                <li>7:35 a.m. PT for sides and totals</li>
                <li>Unplanned releases closer to tipoff of games</li>
              </ul>
              {/* <div className="relative mt-6 flex items-start">
                <div className="flex h-6 items-center">
                  <input
                    id="guarantee"
                    aria-describedby="guarantee-description"
                    name="guarantee"
                    type="checkbox"
                    className="h-6 w-6 rounded border-zinc-400 text-emerald-600 focus:ring-emerald-600 disabled:bg-zinc-100 disabled:dark:bg-zinc-800"
                    checked={guarantee}
                    disabled
                    onChange={handleGuarnateeChange}
                  />
                </div>
                <div className="ml-3 text-sm leading-6">
                  <label
                    htmlFor="guarantee"
                    className="font-medium text-zinc-900 line-through dark:text-zinc-300"
                  >
                    Add money-back guarantee 54.5% results or better for $500
                  </label>
                  <div
                    id="guarantee-description"
                    className="text-xs text-zinc-500 dark:text-zinc-400"
                  >
                    *Original offer no longer available.{' '}
                    <a
                      href="/support-chat"
                      className="mt-1 block text-zinc-400 underline dark:text-zinc-600"
                    >
                      Contact us for guaranteed options
                    </a>
                  </div>
                </div>
              </div> */}
              <div className="mt-10 flex items-center gap-x-4">
                <h4 className="flex-none text-sm font-semibold leading-6 text-emerald-600 dark:text-emerald-500">
                  What’s included
                </h4>
                <div className="h-px flex-auto bg-zinc-100 dark:bg-zinc-700" />
              </div>
              <ul
                role="list"
                className="mt-8 grid grid-cols-1 gap-4 text-sm leading-6 text-zinc-600 dark:text-zinc-400 sm:grid-cols-2 sm:gap-6"
              >
                <li className="flex gap-x-3">
                  <svg
                    className="h-6 w-5 flex-none text-emerald-600 dark:text-emerald-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  Regular season remainder
                </li>
                <li className="flex gap-x-3">
                  <svg
                    className="h-6 w-5 flex-none text-emerald-600 dark:text-emerald-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  Conference tournaments
                </li>
                <li className="flex gap-x-3">
                  <svg
                    className="h-6 w-5 flex-none text-emerald-600 dark:text-emerald-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  NCAA tournament
                </li>
                <li className="flex gap-x-3">
                  <svg
                    className="h-6 w-5 flex-none text-emerald-600 dark:text-emerald-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  NIT & CBI tournaments
                </li>
              </ul>
            </div>
            <div className="-mt-6 p-6 lg:mt-0 lg:w-full lg:max-w-xs lg:flex-shrink-0">
              <div className="h-full rounded bg-zinc-50 py-10 text-center ring-1 ring-inset ring-zinc-900/5 dark:bg-zinc-700 dark:ring-zinc-50/5 lg:flex lg:flex-col lg:justify-center lg:py-16">
                <div className="space-y-12 px-8">
                  <PriceInfo
                    locale={locale}
                    variety={offer}
                    stats={stats}
                    {...calculatePrice({
                      discount,
                      stats: stats || baseStats,
                      variety: offer || baseOffer,
                    })}
                    // playsFor={offer?.plays + (offer?.addOn ? offer?.addOn?.plays : 0) || 0}
                  />
                  <>
                    <div style={{ position: 'relative', zIndex: 10 }}>
                      {initialState?.currentUser && (
                        <>
                          <ul className="space-y-2">
                            <li>
                              <StripeButton
                                product={product}
                                index={offers.indexOf(offer as API.MembershipProductOffer)}
                                price={
                                  calculatePrice({
                                    discount,
                                    stats: stats || baseStats,
                                    variety: offer || baseOffer,
                                  }).price
                                }
                                size="large"
                                source="order"
                                // plays={offer?.plays + (offer?.addOn ? offer?.addOn?.plays : 0) || 0}
                              />
                            </li>
                            <li>
                              <BitcoinButton
                                user={initialState.currentUser}
                                product={product}
                                index={offers.indexOf(offer as API.MembershipProductOffer)}
                                fullWidth={true}
                              />
                            </li>
                          </ul>
                          <div className="mt-4 text-xs text-zinc-500 dark:text-zinc-400">
                            <p>
                              Existing CBB plays still apply toward this service. Interested in
                              upgrading to the season pass?
                            </p>
                            <p className="mt-1">
                              <a href="/support-chat">Send us a message</a>
                            </p>
                          </div>
                        </>
                      )}
                      {!initialState?.currentUser && (
                        <a
                          href={`/register?redirect=/order${
                            window.location.pathname === '/cfb' ? '?sport=cfb' : ''
                          }`}
                        >
                          <button className="flex w-full cursor-pointer justify-center rounded border-0 bg-emerald-500 px-3 py-2.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-emerald-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 disabled:cursor-not-allowed disabled:opacity-50 data-[loading=true]:animate-pulse data-[loading=true]:cursor-progress dark:bg-emerald-500 hover:dark:bg-emerald-400">
                            Sign in to order
                          </button>
                        </a>
                      )}
                    </div>
                  </>
                  {product.offers[0]?.ui?.prorated && (
                    <div className={styles.prorated}>
                      *Prorated {product.offers[0]?.ui?.prorated}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        ''
      )}
    </>
  );
};

const CBBStarterProduct: React.FC<{
  locale: string;
  product: API.MembershipProduct;
  discount: number;
  initialState: { currentUser?: API.User | null | undefined };
}> = ({ locale, product, discount, initialState }) => {
  const { offers, stats } = product;
  // const [offer] = offers;
  const [offer, setOffer] = useState<API.MembershipProductOffer | undefined>(offers[1]);
  const [quantities, setQuantities] = useState(new Set<number>());
  const [selectedQuantity, setSelectedQuantity] = useState(10);
  // const [services, setServices] = useState(new Set());
  // const [starterAddOn, setStarterAddOn] = useState(true);
  // console.log(product);

  const includes = [
    {
      title: 'Primarily extra rotation sides & totals',
      description: 'Released at 2 p.m. PT weekdays, TBD on weekends',
    },
    {
      title: 'Main board late releases & 2nd half plays',
      description: 'Includes weekend main board late releases and all 2nd half plays',
    },
  ];

  useEffect(() => {
    for (const productOffer of offers) {
      setQuantities(
        (currentState) =>
          new Set(
            currentState.add(productOffer.plays - (productOffer?.auto && product?.stats?.played)),
          ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers]);

  useEffect(() => {
    const selectedOffer = offers.find(
      (ofr) => selectedQuantity === ofr.plays - (ofr?.auto && product?.stats?.played),
    );

    setOffer(selectedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers, selectedQuantity]);

  return (
    <>
      {offer ? (
        <>
          <div className="col-span-12 rounded bg-white p-8 shadow-lg ring-1 ring-zinc-900/5 dark:bg-white/5 dark:shadow-none dark:ring-white/20 lg:col-span-6 xl:col-span-5">
            <span className="text-lg font-semibold">{product.title}</span>
            <p className="mb-8 mt-4 text-zinc-600 dark:text-zinc-400">{product?.description}</p>
            <PriceInfo
              locale={locale}
              variety={offer}
              stats={stats}
              {...calculatePrice({
                discount,
                stats: stats || baseStats,
                variety: offer || baseOffer,
              })}
              playsFor={offer?.plays - (offer?.auto ? stats?.played : 0)}
            />
            <RadioGroup
              value={selectedQuantity}
              onChange={setSelectedQuantity}
              className={styles.radioGroup}
            >
              {/* <RadioGroup.Label className="sr-only">Plays</RadioGroup.Label> */}
              <div className="mb-4 mt-6 grid grid-cols-3 gap-3">
                {[...quantities].map((option) => (
                  <RadioGroup.Option
                    key={option}
                    value={option}
                    className={({ active, checked }) =>
                      clsx(
                        // option.available ? `${styles.available}` : `${styles.unavailable}`,
                        active
                          ? 'ring-2 ring-emerald-500 ring-offset-2 dark:ring-offset-zinc-800'
                          : '',
                        checked
                          ? 'border border-emerald-500 text-emerald-500 hover:bg-zinc-50 dark:border-emerald-400 dark:bg-white/10 dark:text-emerald-400 dark:hover:bg-white/20'
                          : 'bg-white text-zinc-900 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-50 dark:bg-white/10 dark:text-white dark:ring-white/20 dark:hover:bg-white/20',
                        'flex cursor-pointer items-center justify-center rounded p-3 text-sm font-semibold sm:flex-1',
                      )
                    }
                    // disabled={!option.available}
                  >
                    <RadioGroup.Label as="span">
                      {option} play{option > 1 ? 's' : ''}
                    </RadioGroup.Label>
                  </RadioGroup.Option>
                ))}
              </div>
            </RadioGroup>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <>
                <div style={{ position: 'relative', zIndex: 10 }}>
                  {initialState?.currentUser && (
                    <Row gutter={[10, 10]}>
                      <Col xs={24} sm={16} md={18}>
                        <StripeButton
                          product={product}
                          index={offers.indexOf(offer as API.MembershipProductOffer)}
                          price={
                            calculatePrice({
                              discount,
                              stats: stats || baseStats,
                              variety: offer || baseOffer,
                            }).price
                          }
                          size="large"
                          source="order"
                          plays={offer?.plays - (offer?.auto ? stats?.played : 0)}
                        />
                      </Col>
                      <Col xs={24} sm={8} md={6}>
                        <BitcoinButton
                          user={initialState.currentUser}
                          product={product}
                          index={offers.indexOf(offer as API.MembershipProductOffer)}
                          fullWidth={true}
                        />
                      </Col>
                    </Row>
                  )}
                  {!initialState?.currentUser && (
                    <a
                      href={`/register?redirect=/order${
                        window.location.pathname === '/cfb' ? '?sport=cfb' : ''
                      }`}
                    >
                      <button className="flex w-full cursor-pointer justify-center rounded border-0 bg-emerald-500 px-3 py-2.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-emerald-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 disabled:cursor-not-allowed disabled:opacity-50 data-[loading=true]:animate-pulse data-[loading=true]:cursor-progress dark:bg-emerald-500 hover:dark:bg-emerald-400">
                        Sign in to order
                      </button>
                    </a>
                  )}
                </div>
              </>
            </Space>
            {/* <div className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
                Have any questions about the service?{' '}
                <a
                  href="https://calendly.com/raspicks/ras-service-discussion?month=2023-08"
                  target="_blank"
                  rel="noreferrer"
                >
                  Book a meeting
                </a>
              </div> */}
            <div className={styles.cbbIncludes}>
              {includes.map(({ title, description }) => (
                <div key={title} className={styles.includesRow}>
                  <CheckIcon
                    className="h-6 w-auto shrink-0 text-emerald-500 dark:text-emerald-400"
                    aria-hidden="true"
                  />
                  <div className={styles.includesItem}>
                    <span className="font-semibold">{title}</span>
                    <span className="inline-block text-zinc-500 dark:text-zinc-400">
                      {description}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {product.offers[0]?.prorated && (
              <div className={styles.prorated}>*Prorated {product.offers[0]?.prorated}</div>
            )}
          </div>
          {/* <Col xs={24} lg={7}>
            <div className="mt-8">
              <div className="my-8 pl-0">
                <h4 className="mb-2 text-xs font-semibold">Release Window Options</h4>
                <p className="mb-4 text-xs text-zinc-500 dark:text-zinc-400">
                  Opt out of times that don't work for you
                </p>
                <div className="flex flex-nowrap items-start justify-start">
                  <ClockIcon
                    className="h-6 w-auto shrink-0 dark:text-zinc-300"
                    aria-hidden="true"
                  />
                  <div className="mb-4 ml-2 flex flex-1 flex-col">
                    <span className={styles.includesTitle}>Weekday early sides</span>
                    <span className="text-zinc-500 dark:text-zinc-400">7 a.m. PT</span>
                  </div>
                </div>
                <div className="flex flex-nowrap items-start justify-start">
                  <ClockIcon
                    className="h-6 w-auto shrink-0 dark:text-zinc-300"
                    aria-hidden="true"
                  />
                  <div className="mb-4 ml-2 flex flex-1 flex-col">
                    <span className={styles.includesTitle}>Weekday early totals</span>
                    <span className="text-zinc-500 dark:text-zinc-400">Between 8-10 a.m. PT</span>
                  </div>
                </div>
                <div className="flex flex-nowrap items-start justify-start">
                  <ClockIcon
                    className="h-6 w-auto shrink-0 dark:text-zinc-300"
                    aria-hidden="true"
                  />
                  <div className="mb-4 ml-2 flex flex-1 flex-col">
                    <span className={styles.includesTitle}>Weekday late windows</span>
                    <span className="text-zinc-500 dark:text-zinc-400">2 p.m. PT or later</span>
                  </div>
                </div>
                <div className="flex flex-nowrap items-start justify-start">
                  <ClockIcon
                    className="h-6 w-auto shrink-0 dark:text-zinc-300"
                    aria-hidden="true"
                  />
                  <div className="mb-4 ml-2 flex flex-1 flex-col">
                    <span className={styles.includesTitle}>Weekend sides</span>
                    <span className="text-zinc-500 dark:text-zinc-400">7 a.m. PT</span>
                  </div>
                </div>
                <div className="flex flex-nowrap items-start justify-start">
                  <ClockIcon
                    className="h-6 w-auto shrink-0 dark:text-zinc-300"
                    aria-hidden="true"
                  />
                  <div className="mb-4 ml-2 flex flex-1 flex-col">
                    <span className={styles.includesTitle}>Weekend totals</span>
                    <span className="text-zinc-500 dark:text-zinc-400">8-9 a.m. PT or later</span>
                  </div>
                </div>
              </div>
              <h4 className="mb-2 text-xs font-semibold">How it works</h4>
              <div className="flex flex-nowrap items-start justify-start">
                <BellIcon className="h-6 w-auto shrink-0 dark:text-zinc-300" aria-hidden="true" />
                <div className="mb-4 ml-2 flex flex-1 flex-col">
                  <span className="text-zinc-500 dark:text-zinc-400">
                    Release warnings will be texted and emailed instructing you when to log in to
                    receive plays
                  </span>
                </div>
              </div>
              <div className="flex flex-nowrap items-start justify-start">
                <MapPinIcon className="h-6 w-auto shrink-0 dark:text-zinc-300" aria-hidden="true" />
                <div className="mb-4 ml-2 flex flex-1 flex-col">
                  <span className="text-zinc-500 dark:text-zinc-400">
                    Plays will only be released at raspicks.com (They will not be texted, emailed or
                    on the RAS app)
                  </span>
                </div>
              </div>
              <div className="flex flex-nowrap items-start justify-start">
                <PauseCircleIcon
                  className="h-6 w-auto shrink-0 dark:text-zinc-300"
                  aria-hidden="true"
                />
                <div className="mb-4 ml-2 flex flex-1 flex-col">
                  <span className="text-zinc-500 dark:text-zinc-400">
                    Service can be paused when you're unavailable. You will receive credit for
                    unused plays.
                  </span>
                </div>
              </div>
              <div className="flex flex-nowrap items-start justify-start">
                <ArrowPathIcon
                  className="h-6 w-auto shrink-0 dark:text-zinc-300"
                  aria-hidden="true"
                />
                <div className="mb-4 ml-2 flex flex-1 flex-col">
                  <span className="text-zinc-500 dark:text-zinc-400">
                    Unused plays can be refunded at any time.
                  </span>
                </div>
              </div>
            </div>
          </Col> */}
        </>
      ) : (
        ''
      )}
    </>
  );
};

const CBB: React.FC<{
  locale: string;
  products?: API.MembershipProduct[];
  discount: number;
  initialState: { currentUser?: API.User | null | undefined };
}> = ({ locale, products, discount, initialState }) => {
  const [endOfSeasonProduct, setEndOfSeasonProduct] = useState(null);

  useEffect(() => {
    products?.forEach((product) => {
      if (product?.offers[0].key === 'end-of-season') {
        setEndOfSeasonProduct(product);
      }
    });
  }, [products]);

  return (
    <>
      {products?.length ? (
        <>
          <div className="grid grid-cols-12 justify-items-stretch gap-4">
            {endOfSeasonProduct && (
              <OffersProduct
                locale={locale}
                product={endOfSeasonProduct}
                discount={discount}
                initialState={initialState}
              />
            )}
            {/* {starterProduct && (
              <CBBStarterProduct
                locale={locale}
                product={starterProduct}
                discount={discount}
                initialState={initialState}
              />
            )} */}
          </div>
          <p className="mt-8 border-t border-zinc-900/10 pt-8 text-xs text-zinc-500 dark:border-white/10 dark:text-zinc-400">
            Disclaimer: It should be expected that lines will move within 3-5 seconds of plays being
            released. Despite our long-term history of winning (56% across thousands of plays), we
            are not immune to variance, poor streaks, etc. Manage your bankroll and expectations
            accordingly. Proper bankroll size, available outs, time and skills are needed to be
            successful and justify the cost of this service.
          </p>
          <h4 className="mb-4 mt-8 text-lg font-semibold">How it works</h4>
          <div className="flex flex-nowrap items-start justify-start">
            <BellIcon className="h-6 w-auto shrink-0 dark:text-zinc-300" aria-hidden="true" />
            <div className="mb-4 ml-2 flex flex-1 flex-col">
              <span className="text-zinc-500 dark:text-zinc-400">
                Release warnings will be texted and emailed instructing you when to log in to
                receive plays
              </span>
            </div>
          </div>
          <div className="flex flex-nowrap items-start justify-start">
            <MapPinIcon className="h-6 w-auto shrink-0 dark:text-zinc-300" aria-hidden="true" />
            <div className="mb-4 ml-2 flex flex-1 flex-col">
              <span className="text-zinc-500 dark:text-zinc-400">
                Plays will only be released at raspicks.com (They will not be texted, emailed or on
                the RAS app)
              </span>
            </div>
          </div>
          <div className="flex flex-nowrap items-start justify-start">
            <PauseCircleIcon
              className="h-6 w-auto shrink-0 dark:text-zinc-300"
              aria-hidden="true"
            />
            <div className="mb-4 ml-2 flex flex-1 flex-col">
              <span className="text-zinc-500 dark:text-zinc-400">
                Service can be paused when you're unavailable. You will receive credit for unused
                plays.
              </span>
            </div>
          </div>
          <div className="flex flex-nowrap items-start justify-start">
            <ArrowPathIcon className="h-6 w-auto shrink-0 dark:text-zinc-300" aria-hidden="true" />
            <div className="mb-4 ml-2 flex flex-1 flex-col">
              <span className="text-zinc-500 dark:text-zinc-400">
                Unused plays can be refunded at any time.
              </span>
            </div>
          </div>
          <div className="prose mt-8 text-xs dark:text-zinc-400">
            <h3 className="dark:text-zinc-200">
              *Money-back guarantee terms and conditions (offer ended Feb. 28)
            </h3>
            <p>
              <i>Updated Feb. 25, 2024</i>
            </p>
            <ul>
              <li>Offer only available until first release in February 2024.</li>
              <li>Service with guarantee is not pausable or refundable.</li>
              <li>
                The win rate will be measured after exactly the first 55 releases. Additional
                releases will not be counted.
              </li>
              <li>
                While we expect the majority of releases to be full game sides and totals from the
                main board, the record used to calculate win rate will include all play types
                released including 1st half plays, 2nd half plays and extra games.
              </li>
              <li>
                If the win rate for these plays fails to reach 54.5% or better, you will be entitled
                to a full refund for this service.
              </li>
              <li>All plays will be graded using the official RAS release line.</li>
            </ul>
          </div>
        </>
      ) : (
        <div>No CBB services currently available.</div>
      )}
    </>
  );
};

const Order: React.FC<{
  defaultTab?: string;
}> = ({ defaultTab }) => {
  const pageContext = usePageContext();
  const locale =
    typeof navigator === 'undefined'
      ? pageContext.request?.headers?.['accept-language']?.split(',')[0] || 'en-US'
      : navigator.language;
  const initialState = { currentUser: useMe() };
  const productsRequest = useRequest(getProducts, { skipRefresh: true });
  const products = productsRequest.data;
  const servicesRequest = useRequest(getServices, { skipRefresh: true });
  const {
    data: orders,
    error: _ordersError,
    loading: _isOrdersLoading,
  } = useRequest(getMyOrders, { skipRefresh: true, hideAlert: true });

  const [nflProducts, setNflProducts] = useState<undefined | API.MembershipProduct[]>(undefined);
  const [cbbProducts, setCBBProducts] = useState<undefined | API.MembershipProduct[]>(undefined);

  useEffect(() => {
    if (products) {
      const foundNflProducts = products
        ?.filter((product) => product.offers?.find((offer) => offer.sport === 'nfl'))
        .sort((a, b) => {
          if ((a.priority || 0) < (b.priority || 0)) {
            return 1;
          }

          return 0;
        });

      if (foundNflProducts) {
        setNflProducts(foundNflProducts);
      }

      const foundCbbProducts = products
        ?.filter((product) => product.offers?.find((offer) => offer.sport === 'cbb'))
        .sort((a, b) => {
          if ((a.priority || 0) < (b.priority || 0)) {
            return 1;
          }

          return 0;
        });

      if (foundCbbProducts) {
        setCBBProducts(foundCbbProducts);
      }
    }
  }, [products]);

  const discount = initialState?.currentUser?.discount || 0;

  const error = servicesRequest.error || productsRequest.error;
  const isLoading = servicesRequest.loading || productsRequest.loading;

  const refresh = () => {
    productsRequest.run?.();
    servicesRequest.run?.();
  };

  useEffect(() => {
    refresh();

    setTimeout(() => {
      const urlProductId = new URLSearchParams(window.location.search).get('productId');

      if (urlProductId) {
        document
          ?.getElementById(`stripe_button_${urlProductId.replace('.', '_')}`)
          ?.scrollIntoView({ behavior: 'smooth' });
      }
    }, 900);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  interface Tab {
    key: string;
    title: string;
    shortTitle: string;
    content?: JSX.Element;
  }

  const TABS: Tab[] = [
    {
      key: 'cbb',
      title: 'College basketball',
      shortTitle: 'CBB',
      content: (
        <CBB
          locale={locale}
          products={cbbProducts}
          discount={discount}
          initialState={initialState}
        />
      ),
    },
    {
      key: 'nfl',
      title: 'NFL',
      shortTitle: 'NFL',
      content: (
        <NFL
          locale={locale}
          products={nflProducts}
          discount={discount}
          initialState={initialState}
          orders={orders}
        />
      ),
    },
  ];

  const sport = new URLSearchParams(window.location.search).get('sport');

  const activeTab = sport || defaultTab;

  const [selectedIndex, setSelectedIndex] = useState(
    TABS.findIndex((tab) => tab.key === activeTab) || 0,
  );

  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-start overflow-hidden">
      <Header title="Order" className="sm:hidden" />
      <div className="overflow-y-auto py-8 dark:bg-none">
        <div className="mx-auto max-w-7xl">
          <div className="hidden px-4 sm:block sm:px-6 lg:px-8">
            <div className="pb-8">
              <h1 className="text-lg font-bold text-zinc-900 dark:text-zinc-200 sm:text-xl">
                Order
              </h1>
            </div>
          </div>
          <div className="px-4 sm:px-6 lg:px-8">
            {
              <Spin className={styles.spin} spinning={isLoading} delay={100}>
                {error && <span>{error.toString()}</span>}

                {!isLoading && !products?.length && (
                  <Row justify="center" gutter={16} wrap={true} className={styles.product}>
                    <Empty description="We're sorry, we were unable to find any products available at the moment." />
                  </Row>
                )}

                {!isLoading && !!products?.length && (
                  <>
                    {!!initialState?.currentUser?.balance && (
                      <div className="rounded bg-blue-50 p-4 text-center text-blue-900 shadow-sm dark:bg-blue-400/20 dark:text-blue-400 lg:flex lg:flex-row">
                        <div className="md:px-8 lg:mr-8 lg:flex-1 lg:border-r lg:border-blue-900/50 lg:dark:border-blue-400/50">
                          <div className="whitespace-nowrap text-sm font-semibold">
                            Account balance
                          </div>
                          <span className="text-3xl">
                            ${initialState.currentUser?.balance?.toFixed(2)}
                          </span>
                        </div>
                        <div className="mx-auto mt-2 max-w-prose text-xs lg:text-left">
                          Account balance will automatically be applied toward your order. Listed
                          prices do not reflect this reduction, but it will be applied at purchase
                          time. Any remaining balance will stay on your account.
                        </div>
                      </div>
                    )}
                    {/* <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
                      <Tab.List className="isolate mx-auto my-8 flex w-5/6 divide-x divide-gray-200 rounded-lg shadow">
                        {TABS.map(({ title, shortTitle }, index) => (
                          <Tab as={Fragment} key={index}>
                            {({ selected }) => (
                              <button
                                className={twMerge(
                                  'group relative min-w-0 flex-1 overflow-hidden bg-zinc-100 px-4 py-4 text-center text-sm font-medium hover:bg-emerald-400 hover:text-white focus:z-10 dark:bg-white/10 dark:hover:bg-white/20',
                                  index === 0 && 'rounded-l-lg',
                                  index === TABS.length - 1 && 'rounded-r-lg',
                                  selected
                                    ? 'bg-emerald-500 text-white hover:bg-emerald-400 dark:bg-emerald-500 dark:hover:bg-emerald-400'
                                    : 'text-zinc-500 dark:text-zinc-300',
                                )}
                              >
                                <span className="sm:hidden">{shortTitle}</span>
                                <span className="hidden sm:inline-block">{title}</span>
                              </button>
                            )}
                          </Tab>
                        ))}
                      </Tab.List>
                      <Tab.Panels>
                        {TABS.map(({ content }, index) => (
                          <Tab.Panel key={index}>{content}</Tab.Panel>
                        ))}
                      </Tab.Panels>
                    </Tab.Group>

                    <Tabs
                      defaultActiveKey={activeTab}
                      items={[
                        {
                          label: 'College basketball',
                          key: 'cbb',
                          children: (
                            <CBB
                              locale={locale}
                              products={cbbProducts}
                              discount={discount}
                              initialState={initialState}
                            />
                          ),
                        },
                        {
                          label: 'NFL',
                          key: 'nfl',
                          children: (
                            <NFL
                              locale={locale}
                              products={nflProducts}
                              discount={discount}
                              initialState={initialState}
                              orders={orders}
                            />
                          ),
                        },
                      ]}
                      size="large"
                      tabBarStyle={{ borderBottom: '1px solid #cbd5e1' }}
                    /> */}
                    <CBB
                      locale={locale}
                      products={cbbProducts}
                      discount={discount}
                      initialState={initialState}
                    />
                  </>
                )}
              </Spin>
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default Order;
